import React, { useMemo, useRef, useState, useEffect } from 'react';
import styles from './RoutesNestedTable.module.css';

const STATUS_COLOR = {
  'Закрыта': '#16a34a',        // green
  'Открыта': '#2563eb',        // blue
  'Не доставлено': '#ef4444',  // red
};
const colorByStatus = (s) => STATUS_COLOR[s] || '#6b7280';

const fmtTime = (v) => {
  const d = v ? new Date(v) : null;
  return d && !isNaN(d) ? d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }) : '—';
};
const toHour = (d) => d.getHours() + d.getMinutes() / 60 + d.getSeconds() / 3600;
const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

const getRouteProgress = (receipts = []) => {
  const total = receipts.length;
  const delivered = receipts.filter(r => r?.status === 'Закрыта').length;
  const opened = receipts.filter(r => r?.status === 'Открыта').length;
  const undelivered = receipts.filter(r => r?.status === 'Не доставлено').length;
  return { total, delivered, opened, undelivered };
};

/**
 * Время "скана" квитанции:
 * 1) r.marks_changed_at (основной источник)
 * 2) максимум по продуктам: marksChangedAt/marks_changed_at/updatedAt (резерв)
 * 3) r.updated_at — ВРЕМЕННЫЙ резерв. TODO: убрать позже, когда появятся стабильные метки скана.
 */
const getScanTime = (r) => {
  if (!r) return null;

  if (r.marks_changed_at) {
    const d = new Date(r.marks_changed_at);
    if (!isNaN(d)) return d;
  }

  const prods = Array.isArray(r.products) ? r.products : [];
  const ts = prods
    .map(p => p?.marksChangedAt ?? p?.marks_changed_at ?? p?.updatedAt ?? null)
    .filter(Boolean)
    .map(v => (typeof v === 'number' ? v : Date.parse(String(v))))
    .filter(Number.isFinite);

  if (ts.length) return new Date(Math.max(...ts));

  // ВРЕМЕННО: используем updated_at квитанции как fallback
  if (r.updated_at) {
    const d = new Date(r.updated_at);
    if (!isNaN(d)) return d;
  }
  return null;
};

const getStatusTime = (r) => {
  if (!r?.status_changed_at) return null;
  const d = new Date(r.status_changed_at);
  return isNaN(d) ? null : d;
};

const pickTickMinutes = (span) => {
  if (span > 12) return 60;
  if (span > 6)  return 30;
  if (span > 3)  return 15;
  if (span > 1)  return 5;
  return 1;
};

export default function RoutesNestedTable({
  routes = [],
  expanded,                   // Set(id)
  onToggle,                   // (routeId)=>void
  onPersonClick,              // (name)=>void
  title = 'Точки / лесенка по маршрутам',
}) {
  const [collapsedMode, setCollapsedMode] = useState('status'); // 'status'|'scan'
  const [win, setWin] = useState({ start: 0, end: 24 });
  const span = win.end - win.start;

  // Собираем все часы для «Подогнать»
  const allHours = useMemo(() => {
    const hrs = [];
    routes.forEach(r => (r.receipts || []).forEach(rcp => {
      const t1 = getStatusTime(rcp), t2 = getScanTime(rcp);
      if (t1) hrs.push(toHour(t1));
      if (t2) hrs.push(toHour(t2));
    }));
    return hrs.sort((a,b)=>a-b);
  }, [routes]);

  const fitToData = () => {
    if (!allHours.length) { setWin({ start: 0, end: 24 }); return; }
    const pad = 0.5;
    const minH = Math.max(0, Math.floor(allHours[0] * 2) / 2 - pad);
    const maxH = Math.min(24, Math.ceil(allHours[allHours.length - 1] * 2) / 2 + pad);
    const minSpan = 1;
    const start = clamp(minH, 0, 24 - minSpan);
    const end = clamp(Math.max(maxH, start + minSpan), start + minSpan, 24);
    setWin({ start, end });
  };
  useEffect(() => { if (allHours.length) fitToData(); /* one-shot per dataset */ }, [allHours.length]); // eslint-disable-line

  const ticks = useMemo(() => {
    const step = pickTickMinutes(span);
    const startM = Math.ceil((win.start * 60) / step) * step;
    const endM = Math.floor((win.end * 60) / step) * step;
    const arr = [];
    for (let m = startM; m <= endM; m += step) {
      const h = m / 60;
      arr.push({ h, label: `${String(Math.floor(h)).padStart(2,'0')}:${String(m % 60).padStart(2,'0')}` });
    }
    return arr;
  }, [win.start, win.end, span]);

  const drag = useRef({ active: false, startX: 0, rectW: 1, start: 0, end: 24 });
  const toPct = (hour) => `${((clamp(hour, win.start, win.end) - win.start) / span) * 100}%`;

const onWheelGroup = (e) => {
  const zoomGesture = e.shiftKey;
  const panGesture  = false;

  if (!zoomGesture) return;

  e.preventDefault();
  e.stopPropagation();

  const rect = e.currentTarget.getBoundingClientRect();
  const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));

  // ЗУМ (Shift + колесо)
  const factor = 1 + Math.sign(e.deltaY) * 0.15;
  const newSpan = clamp(span * factor, 0.25, 24);
  const center = win.start + frac * span;

  let start = center - frac * newSpan;
  let end = start + newSpan;
  if (start < 0) { end -= start; start = 0; }
  if (end > 24) { start -= (end - 24); end = 24; }

  setWin({ start, end });
};


  const onMouseDownGroup = (e) => {
    if (span >= 24) return;
    const rect = e.currentTarget.getBoundingClientRect();
    drag.current = { active: true, startX: e.clientX, rectW: rect.width, start: win.start, end: win.end };
    e.currentTarget.classList.add(styles.dragging);
  };
  const onMouseMoveGroup = (e) => {
    if (!drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    const deltaH = (dx / drag.current.rectW) * (drag.current.end - drag.current.start);
    let start = drag.current.start - deltaH;
    let end = drag.current.end - deltaH;
    if (start < 0) { end -= start; start = 0; }
    if (end > 24) { start -= (end - 24); end = 24; }
    setWin({ start, end });
  };
  const stopDragGroup = (e) => {
    if (!drag.current.active) return;
    drag.current.active = false;
    e.currentTarget.classList.remove(styles.dragging);
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.title}>{title}</div>
        <div className={styles.controls}>
          <span className={styles.dim}>Точки:</span>
          <label className={styles.switch}>
            <input type="radio" name="tlmode" value="status"
              checked={collapsedMode === 'status'} onChange={() => setCollapsedMode('status')} />
            <span>по статусам</span>
          </label>
          <label className={styles.switch}>
            <input type="radio" name="tlmode" value="scan"
              checked={collapsedMode === 'scan'} onChange={() => setCollapsedMode('scan')} />
            <span>по сканам</span>
          </label>
          <button className={styles.smallBtn} onClick={() => setWin({ start: 0, end: 24 })}>Сброс</button>
          <button className={styles.smallBtn} onClick={fitToData}>Подогнать</button>
          <span className={styles.dim}>{win.start.toFixed(1)}–{win.end.toFixed(1)} ч</span>
        </div>
      </div>

      <div className={styles.list}>
        {routes.map((r) => {
          const isOpen = expanded?.has?.(r.id);
          const receipts = Array.isArray(r.receipts) ? r.receipts : [];
          const prog = getRouteProgress(receipts);
          const deliveredPct = prog.total ? Math.round((prog.delivered   / prog.total) * 100) : 0;
          const openedPct    = prog.total ? Math.round((prog.opened      / prog.total) * 100) : 0;
          const undeliveredPct = prog.total ? Math.round((prog.undelivered / prog.total) * 100) : 0;

          const collapsedPoints = receipts
            .map(rcp => {
              const t = collapsedMode === 'status' ? getStatusTime(rcp) : getScanTime(rcp);
              return t ? { h: toHour(t), rcp } : null;
            })
            .filter(Boolean)
            .filter(p => p.h >= win.start && p.h <= win.end)
            .sort((a, b) => a.h - b.h);

          const expandedRows = receipts
            .map((rcp) => {
              const scan = getScanTime(rcp);
              const stat = getStatusTime(rcp);
              const timeKey = scan || stat || null;
              return { rcp, scan, stat, timeKey };
            })
            .sort((a, b) => {
              const at = a.timeKey ? +a.timeKey : 0;
              const bt = b.timeKey ? +b.timeKey : 0;
              return at - bt;
            });

          return (
<div key={r.id} className={styles.groupRow}>
  {/* Левая боковая панель маршрута */}
  <div className={styles.routeSidebar}>
  <div className={styles.routeMeta}>
    <div className={styles.routeLine1}>
      <button
        className={styles.routeNoBtn}
        onClick={() => onToggle?.(r.id)}
        aria-expanded={isOpen}
        title={isOpen ? 'Свернуть' : 'Развернуть'}
      >
        №{r.route_number} {isOpen ? '▾' : '▸'}
      </button>
      {r.time_from && r.time_to && (
        <span className={styles.window}>{r.time_from}–{r.time_to}</span>
      )}
    </div>
      <div className={styles.routeLine2}>
        {r.driver && (
          <button className={styles.link} onClick={() => onPersonClick?.(r.driver)}>{r.driver}</button>
        )}
        {[r.loader1, r.loader2].filter(Boolean).map((name, i) => (
          <button key={i} className={styles.link} onClick={() => onPersonClick?.(name)}>
            {i === 0 ? ' • ' : ', '}{name}
          </button>
        ))}
      </div>

      <div className={styles.progress}>
        <div className={styles.progressBar}>
          <div className={styles.pbDelivered}   style={{ width: `${prog.total ? (prog.delivered   / prog.total) * 100 : 0}%` }} />
          <div className={styles.pbOpened}      style={{ width: `${prog.total ? (prog.opened      / prog.total) * 100 : 0}%` }} />
          <div className={styles.pbUndelivered} style={{ width: `${prog.total ? (prog.undelivered / prog.total) * 100 : 0}%` }} />
        </div>
        <div className={styles.progressLegend}>
          <span>✓ {deliveredPct}%</span>
          <span>В работе {openedPct}%</span>
          <span>Не дост. {undeliveredPct}%</span>
        </div>
      </div>
      
    </div>
  </div>

  {/* Правая часть — шкала */}
  <div
    className={styles.axisHost}
    style={{ '--label-col': isOpen ? '220px' : '0px' }}
    onWheelCapture={onWheelGroup}
    onMouseDown={onMouseDownGroup}
    onMouseMove={onMouseMoveGroup}
    onMouseLeave={stopDragGroup}
    onMouseUp={stopDragGroup}
    role="application"
    aria-label="Шкала времени маршрута"
  >
    {/* содержимое axisHost без изменений */}
    <div className={styles.ticksRowLocal}>
      {ticks.map((t, i) => (
        <div key={i} className={styles.tickLocal} style={{ left: toPct(t.h) }}>
          <span className={styles.tickLabel}>{t.label}</span>
          <span className={styles.vlineLocal} />
        </div>
      ))}
    </div>

    {!isOpen ? (
      <div className={styles.timelineCollapsedInner}>
        {collapsedPoints.map((p, i) => (
          <div
            key={i}
            className={styles.dot}
            style={{ left: toPct(p.h), backgroundColor: colorByStatus(p.rcp.status) }}
            title={`#${p.rcp.receipt_number || p.rcp.id} • ${collapsedMode === 'status' ? 'статус' : 'скан'} ${fmtTime(collapsedMode === 'status' ? p.rcp.status_changed_at : p.rcp.marks_changed_at || p.rcp.updated_at)}`}
          />
        ))}
      </div>
    ) : (
      <div className={styles.timelineExpanded}>
        {expandedRows.map(({ rcp, scan, stat }, idx) => {
          const hScan = scan ? toHour(scan) : null;
          const hStat = stat ? toHour(stat) : null;
          const leftH = (hScan != null && hStat != null) ? Math.min(hScan, hStat) : (hScan ?? hStat ?? 0);
          const rightH = (hScan != null && hStat != null) ? Math.max(hScan, hStat) : leftH;
          const deltaMin = (hScan != null && hStat != null) ? Math.abs(hStat - hScan) * 60 : null;
          const isTiny = deltaMin != null && deltaMin < 1;
          const pointPct = toPct(hStat ?? hScan ?? 0);
          const leftPct = toPct(leftH);
          const widthPct = `${((clamp(rightH, win.start, win.end) - clamp(leftH, win.start, win.end)) / span) * 100}%`;

          return (
            <div key={rcp.id ?? idx} className={styles.recRow}>
              <div className={styles.recLabel} title={rcp.delivery_address || ''}>
                 {rcp.delivery_address || '—'}
              </div>
              <div className={styles.recBarWrap}>
                {(hScan != null && hStat != null && !isTiny) ? (
                  <div
                    className={styles.bar}
                    style={{ left: leftPct, width: widthPct, backgroundColor: colorByStatus(rcp.status) }}
                    title={`Скан: ${fmtTime(scan)} • Статус: ${fmtTime(stat)} • Δ ${deltaMin?.toFixed(0)} мин`}
                  />
                ) : (
                  <div
                    className={styles.dotLarge}
                    style={{ left: pointPct, backgroundColor: colorByStatus(rcp.status) }}
                    title={`${stat ? 'Статус' : 'Скан'}: ${fmtTime(stat ?? scan)}`}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    )}
  </div>
</div>

          );
        })}

        {!routes.length && <div className={styles.empty}>Ничего не найдено</div>}
      </div>
    </div>
  );
}
